// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import constants from "@store/constants";
import hasSameParams from "@store/Framework/Common/Utilities/Events/hasSameParams";
import { getEventParameters } from "@store/Framework/Store/Store/Pages/EventList/EventListSelector";
import getEventList, {
    GetEventsApiParameter,
} from "@store/Framework/Store/Store/Pages/EventList/Handlers/GetEventList";
import EventListTile, { EventListTileStyles } from "@store/Library/Components/Events/EventListTile";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    noResultsMessage = "noResultsMessage",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.noResultsMessage]: string;
        [fields.title]: string;
    };
}

type Props = OwnProps;

export interface EventListViewStyles {
    title?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    wrapper?: InjectableCss;
    noResult?: TypographyPresentationProps;
    tileStyle?: EventListTileStyles;
}

const eventListViewStyles: EventListViewStyles = {
    title: {
        variant: "h2",
        css: css`
            margin: 0;
        `,
    },
    loadingOverlay: {
        css: css`
            ${breakpointValues([`${pxToRem(30)}`, null, `${pxToRem(40)}`], "margin-top")}

            &.contains-isFeatured {
                margin-top: 0;
            }
        `,
    },
    wrapper: {
        css: css`
            &.contains-isFeatured-and-not-isFeatured {
                margin: ${pxToRem(30, 0, 0)};
                padding: ${pxToRem(30, 0, 0)};
                border-top: 1px solid ${getColor("common.border")};
            }
        `,
    },
    tileStyle: {
        wrapper: {
            css: css`
                &:first-child {
                    padding-top: 0;
                }
            `,
        },
    },
};

const styles = eventListViewStyles;

const EventListView: FC<Props> = ({ fields }) => {
    const parameter = useAppSelector(state => getEventParameters(state));
    const dispatch = useDispatch();

    const [apiParameter, setApiParameter] = useState<GetEventsApiParameter>(parameter);
    const baseEventListDataView = useAppSelector(state => getDataView(state.data.pages, apiParameter));
    const eventListDataView = usePersistentDataView(baseEventListDataView);
    const { isLoading, value: eventList } = eventListDataView;
    const currentPage = useAppSelector(state => getPageLinkByNodeId(state, getCurrentPage(state).nodeId));

    useEffect(() => {
        if (!hasSameParams(apiParameter, parameter)) {
            setApiParameter(parameter);
        }
    }, [parameter]);

    useEffect(() => {
        if (!isLoading || !eventList) {
            dispatch(getEventList(apiParameter));
        }
    }, [apiParameter]);

    const containFeatured = eventList && eventList?.filter(page => page.fields.isFeatured).length > 0;
    const containNotFeatured = eventList && eventList?.filter(page => !page.fields.isFeatured).length > 0;

    return (
        <DataViewLoadingOverlay
            dataView={eventListDataView}
            {...styles.loadingOverlay}
            className={containFeatured ? "contains-isFeatured" : ""}
        >
            {eventList?.length ? (
                <StyledWrapper
                    {...styles.wrapper}
                    className={containFeatured && containNotFeatured ? "contains-isFeatured-and-not-isFeatured" : ""}
                >
                    {containNotFeatured && fields.title && <Typography {...styles.title}>{fields.title}</Typography>}
                    {eventList.map((page: PageProps) => {
                        const {
                            fields: {
                                isFeatured,
                                urlSegment,
                                startDate,
                                endDate,
                                hideHourMinutes,
                                dateTimezone,
                                isLocalBranch,
                                localBranchTitle,
                                addressWrapper,
                                previewImage,
                                categoriesList,
                                eventSummary,
                                linkLabel,
                            },
                        } = page;

                        if (isFeatured) {
                            return null;
                        }

                        return (
                            <EventListTile
                                key={page.id}
                                title={page.fields["title"]}
                                titleForwardAs={!fields.title ? "h2" : "h3"}
                                eventLink={`${currentPage?.url}/${urlSegment}`}
                                startDate={startDate}
                                endDate={endDate}
                                hideHourMinutes={hideHourMinutes}
                                dateTimezone={dateTimezone}
                                isLocalBranch={isLocalBranch}
                                localBranchTitle={localBranchTitle}
                                addressWrapper={addressWrapper}
                                previewImage={previewImage}
                                categoriesList={categoriesList}
                                eventSummary={eventSummary}
                                linkLabel={linkLabel}
                                extendedStyles={styles.tileStyle}
                            />
                        );
                    })}
                </StyledWrapper>
            ) : (
                <Typography {...styles.noResult}>{fields.noResultsMessage}</Typography>
            )}
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: EventListView,
    definition: {
        group: constants.widgetGroup.eventList,
        displayName: "Event List View",
        allowedContexts: ["EventListPage"] as any,
        fieldDefinitions: [
            {
                name: fields.noResultsMessage,
                displayName: "No Search Results Message",
                defaultValue: "There are no events matching the current search criteria.",
                editorTemplate: "MultilineTextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Upcoming Events",
                fieldType: "Translatable",
                placeholder: "Input a title",
            },
        ],
    },
};

export default widgetModule;
